/**
 * Notification preferences. The `notificationPreference` router has existed
 * server-side with no UI at all — this is it.
 *
 * One toggle today, because the server models exactly one category
 * (`system`). The layout is a list so adding the next category is a row, not
 * a redesign. Preferences are USER-scoped, not org-scoped: "don't ping me"
 * follows the person across every organisation they belong to.
 */
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

import { Card } from "@gately/ui/card";
import { Switch } from "@gately/ui/switch";
import { toast } from "@gately/ui/toast";

import { PhoneVerifyDialog } from "~/components/account/phone-verify-dialog";
import { orpc } from "~/lib/orpc";
import { maskPhoneWeb } from "~/lib/phone-verify-timers";

export const Route = createFileRoute("/_dashboard/compte/notifications")({
  // `useSuspenseQuery` below needs this, or navigating here remounts the app.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      orpc.notificationPreference.get.queryOptions(),
    ),
  component: NotificationsPage,
});

function NotificationsPage() {
  const queryClient = useQueryClient();
  const queryKey = orpc.notificationPreference.get.key();
  const { data: prefs } = useSuspenseQuery(
    orpc.notificationPreference.get.queryOptions(),
  );

  const update = useMutation(
    orpc.notificationPreference.update.mutationOptions({
      // A switch that waits for a round-trip before moving feels broken, so
      // it moves first and rolls back if the server disagrees.
      onMutate: async (next) => {
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, next);
        return { previous };
      },
      onError: (err, _next, context) => {
        queryClient.setQueryData(queryKey, context?.previous);
        toast.error(err.message || "Échec de l'enregistrement.");
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey }),
    }),
  );

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 px-6 py-8">
      <div>
        <Link
          to="/compte"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm transition-colors"
        >
          <ChevronLeft className="size-4" />
          Mon compte
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight">
          Notifications
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Ce que Gately peut vous envoyer sur vos appareils et sur WhatsApp.
        </p>
      </div>

      <Card className="p-0">
        <div className="flex items-center gap-4 p-4">
          <label htmlFor="pref-system" className="min-w-0 flex-1">
            <span className="block text-sm font-medium">
              Notifications de l'application
            </span>
            <span className="text-muted-foreground block text-xs">
              Visiteurs à l'entrée, validations à traiter, annonces de votre
              gestionnaire.
            </span>
          </label>
          <Switch
            id="pref-system"
            checked={prefs.system}
            onCheckedChange={(system) => update.mutate({ system })}
          />
        </div>
      </Card>

      <WhatsAppCard />
    </div>
  );
}

/** The « Recevoir sur WhatsApp » switch (spec 2026-09-28 §7). Opens
 *  `PhoneVerifyDialog` in `whatsAppOnly` mode whenever turning it on needs a
 *  fresh proof (no contact yet, or the last one lapsed). */
function WhatsAppCard() {
  const qc = useQueryClient();
  const key = orpc.me.whatsapp.get.key();
  const status = useQuery(orpc.me.whatsapp.get.queryOptions());
  const [verifyOpen, setVerifyOpen] = useState(false);
  const set = useMutation(
    orpc.me.whatsapp.set.mutationOptions({
      onSuccess: async (r) => {
        if (r.needsVerification) setVerifyOpen(true);
        await qc.invalidateQueries({ queryKey: key });
      },
      onError: (err) =>
        toast.error(err.message || "Échec de l'enregistrement."),
    }),
  );
  const s = status.data;
  const on = s?.status === "active";
  const line = !s
    ? ""
    : s.status === "no_phone"
      ? "Ajoutez un numéro dans votre profil."
      : s.status === "active"
        ? `Actif sur le ${maskPhoneWeb(s.phoneNumber ?? "")}`
        : s.status === "disabled"
          ? "Désactivé par votre gestionnaire."
          : "Désactivé";
  return (
    <Card className="p-0">
      <div className="flex items-center gap-4 p-4">
        <label htmlFor="pref-whatsapp" className="min-w-0 flex-1">
          <span className="block text-sm font-medium">WhatsApp</span>
          <span className="text-muted-foreground block text-xs">
            Recevoir les arrivées imprévues sur WhatsApp. {line}
          </span>
          {s?.status === "no_phone" ? (
            <Link to="/compte" className="text-primary text-xs underline">
              Ajouter un numéro
            </Link>
          ) : null}
        </label>
        <Switch
          id="pref-whatsapp"
          checked={on}
          disabled={
            !s ||
            s.status === "no_phone" ||
            s.status === "disabled" ||
            set.isPending
          }
          onCheckedChange={(enabled) => set.mutate({ enabled })}
        />
      </div>
      {s?.phoneNumber ? (
        <PhoneVerifyDialog
          open={verifyOpen}
          onOpenChange={(o) => {
            setVerifyOpen(o);
            if (!o) void qc.invalidateQueries({ queryKey: key });
          }}
          currentPhone={s.phoneNumber}
          whatsAppOnly
        />
      ) : null}
    </Card>
  );
}
